import {useFormik} from 'formik'
import {useState} from 'react'
import * as Yup from 'yup'
import {handleAxiosError} from '../../../utils/helperFunction/_helper'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useMutation} from 'react-query'
import {BASE_URL} from '../../config'
import axios from 'axios'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import HCaptchaComponent from './HCaptchaComponent'
import {siteKey} from '../../../App'

const RESET_PASS_URL = `${BASE_URL}/api/verifyMobileAndResetPass`

interface IResetPasswordInputValues {
  otp: number | undefined
  newpass: string
  confirmNewpass: string
}

const initValues: IResetPasswordInputValues = {
  otp: undefined,
  newpass: '',
  confirmNewpass: '',
}

const inputValuesSchema = Yup.object().shape({
  otp: Yup.string().required('Please provide OTP'),
  newpass: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Please provide new password'),
  confirmNewpass: Yup.string()
    .oneOf([Yup.ref('newpass')], 'Passwords must match')
    .required('Please confirm new password'),
})

const resetPassword = async (params: {
  mobileNumber: string
  otp: number
  newpass: string
  captchaResponse: string
}) => {
  const response = await axios.post(
    RESET_PASS_URL,
    {},
    {
      params: {...params},
    }
  )

  return response.data
}

const ResetPasswordInputs = () => {
  const {mobileNumber} = useParams()
  const navigate = useNavigate()

  const [token, setToken] = useState('')

  const {isLoading, mutate} = useMutation({
    mutationFn: resetPassword,
    onSuccess: (data) => {
      toast.dismiss()
      toast.success('Password reset successfully!')
      toast.success('Please log in.')
      navigate(`/auth/login`)
    },
    onError: (error) => {
      toast.dismiss()
      const message = handleAxiosError(error)
      toast.error(message)
    },
  })

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: inputValuesSchema,
    onSubmit: async (values) => {
      try {
        toast.loading('Processing..')

        const sendData = {
          mobileNumber: mobileNumber!,
          otp: values.otp!,
          newpass: values.newpass!,
          captchaResponse: token,
        }

        mutate(sendData)
      } catch (error) {
        toast.dismiss()
        const message = handleAxiosError(error)
        toast.error(message)
      }
    },
  })

  const onHandleCaptcha = (token: string) => {
    console.log(token)
    setToken(token)
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='required form-label fw-bolder text-gray-900 fs-6'>OTP</label>
        <input
          type='number'
          placeholder='Please provide OTP'
          autoComplete='off'
          {...formik.getFieldProps('otp')}
          className={clsx(
            'form-control custom-input',
            {'is-invalid': formik.touched.otp && formik.errors.otp},
            {
              'is-valid': formik.touched.otp && !formik.errors.otp,
            }
          )}
        />
        {formik.touched.otp && formik.errors.otp && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.otp}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='required form-label fw-bolder text-gray-900 fs-6'>New Password</label>
        <input
          type='password'
          placeholder='Enter new password'
          autoComplete='off'
          {...formik.getFieldProps('newpass')}
          className={clsx(
            'form-control custom-input',
            {'is-invalid': formik.touched.newpass && formik.errors.newpass},
            {
              'is-valid': formik.touched.newpass && !formik.errors.newpass,
            }
          )}
        />
        {formik.touched.newpass && formik.errors.newpass && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.newpass}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='required form-label fw-bolder text-gray-900 fs-6'>
          Confirm New Password
        </label>
        <input
          type='password'
          placeholder='Confirm new password'
          autoComplete='off'
          {...formik.getFieldProps('confirmNewpass')}
          className={clsx(
            'form-control custom-input',
            {'is-invalid': formik.touched.confirmNewpass && formik.errors.confirmNewpass},
            {
              'is-valid': formik.touched.confirmNewpass && !formik.errors.confirmNewpass,
            }
          )}
        />
        {formik.touched.confirmNewpass && formik.errors.confirmNewpass && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.confirmNewpass}</span>
            </div>
          </div>
        )}
      </div>

      {/* end::Form group */}
      <div className='my-4'>
        <HCaptchaComponent siteKey={siteKey} onVerify={onHandleCaptcha} />
      </div>

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
          <span className='indicator-label'>Submit</span>
          {isLoading && (
            <span className='indicator-progress'>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancel
          </button>
        </Link>
      </div>
      {/* end::Form group */}
    </form>
  )
}

export default ResetPasswordInputs
